import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft,
  Printer,
  Award,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertCircle,
  Clock,
  DollarSign,
  Package,
  Store,
  Star,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VendorScorecardPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchScorecard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vendors-analytics/scorecard`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to generate scorecard');
      navigate('/vendors');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchScorecard();
  }, [fetchScorecard]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return ''; }
  };

  const gradeColor = (grade) => {
    switch (grade) {
      case 'A': return { bg: 'bg-green-500', text: 'text-green-700', border: 'border-green-500', light: 'bg-green-50' };
      case 'B': return { bg: 'bg-blue-500', text: 'text-blue-700', border: 'border-blue-500', light: 'bg-blue-50' };
      case 'C': return { bg: 'bg-yellow-500', text: 'text-yellow-700', border: 'border-yellow-500', light: 'bg-yellow-50' };
      case 'D': return { bg: 'bg-red-500', text: 'text-red-700', border: 'border-red-500', light: 'bg-red-50' };
      default: return { bg: 'bg-zinc-500', text: 'text-zinc-700', border: 'border-gray-500', light: 'bg-zinc-50' };
    }
  };

  const ScoreBar = ({ value, max = 100, label }) => (
    <div className="flex items-center gap-2">
      <span className="text-xs font-bold w-20 text-right shrink-0 score-bar-label">{label}</span>
      <div className="flex-1 h-4 bg-zinc-100 border border-black overflow-hidden print-bar-container">
        <div
          className={`h-full transition-all ${
            value >= 80 ? 'bg-green-500' : value >= 60 ? 'bg-yellow-500' : 'bg-red-500'
          }`}
          style={{ width: `${Math.min(value, max)}%` }}
        />
      </div>
      <span className="text-xs font-heading font-bold uppercase tracking-wide w-8 score-bar-value">{value}</span>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) return null;

  const topVendor = data.vendors[0];

  return (
    <div className="scorecard-page" data-testid="vendor-scorecard-page">
      {/* Screen-only controls */}
      <div className="p-4 sm:p-6 lg:p-8 print:hidden">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/vendors')}
            data-testid="back-to-vendors-btn"
            className="inline-flex items-center gap-2 text-sm font-bold hover:text-primary"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Vendors
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/vendors-analytics/scorecard/pdf`, {
                    withCredentials: true,
                    responseType: 'blob',
                  });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  const link = document.createElement('a');
                  link.href = url;
                  const disposition = res.headers['content-disposition'];
                  const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'Vendor_Scorecard.pdf';
                  link.setAttribute('download', filename);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Scorecard PDF downloaded!');
                } catch {
                  toast.error('Failed to generate PDF');
                }
              }}
              data-testid="download-scorecard-pdf-btn"
              className="inline-flex items-center gap-2 h-12 px-6 bg-zinc-900 text-white font-bold uppercase border border-zinc-200 hover:bg-zinc-800 active:translate-y-1 transition-all"
            >
              <Download className="w-5 h-5" />
              Download PDF
            </button>
            <button
              onClick={() => window.print()}
              data-testid="print-scorecard-btn"
              className="inline-flex items-center gap-2 h-12 px-6 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200 hover:bg-primary-hover active:translate-y-1 transition-all"
            >
              <Printer className="w-5 h-5" />
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Printable Content */}
      <div className="px-4 sm:px-6 lg:px-8 pb-8 print:px-0 print:pb-0" data-testid="scorecard-content">
        {/* Report Header */}
        <div className="bg-white border border-zinc-200 p-6 sm:p-8  mb-6 print:shadow-none print:border-b-2 print:border-l-0 print:border-r-0 print:border-t-0" data-testid="scorecard-header">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Award className="w-8 h-8 text-primary print:text-zinc-900" />
                <h1 className="text-2xl sm:text-3xl font-heading font-bold uppercase tracking-wide tracking-tight">Vendor Scorecard</h1>
              </div>
              <p className="text-lg font-bold">{data.company_name}</p>
              <p className="text-sm text-zinc-500 mt-1">
                Generated {formatDate(data.generated_at)} &middot; {data.vendor_count} vendor{data.vendor_count !== 1 ? 's' : ''} &middot; {formatCurrency(data.total_company_spend)} total spend
              </p>
            </div>
            <div className="hidden print:block text-right">
              <p className="text-xs text-zinc-500">PUNCHLIST</p>
              <p className="text-xs text-zinc-500">Project Management</p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 print:gap-2 print:mb-4">
          <div className="bg-white border border-zinc-200 p-4  print:shadow-none" data-testid="scorecard-total-vendors">
            <Store className="w-6 h-6 text-primary mb-2 print:text-zinc-900" />
            <p className="text-2xl font-heading font-bold uppercase tracking-wide">{data.vendor_count}</p>
            <p className="text-xs font-bold uppercase text-zinc-500">Active Vendors</p>
          </div>
          <div className="bg-white border border-zinc-200 p-4  print:shadow-none" data-testid="scorecard-total-spend">
            <DollarSign className="w-6 h-6 text-primary mb-2 print:text-zinc-900" />
            <p className="text-2xl font-heading font-bold uppercase tracking-wide">{formatCurrency(data.total_company_spend)}</p>
            <p className="text-xs font-bold uppercase text-zinc-500">Total Spend</p>
          </div>
          <div className="bg-white border border-zinc-200 p-4  print:shadow-none" data-testid="scorecard-top-vendor">
            <Star className="w-6 h-6 text-primary mb-2 print:text-zinc-900" />
            <p className="text-lg font-heading font-bold uppercase tracking-wide truncate">{topVendor?.vendor_name || 'N/A'}</p>
            <p className="text-xs font-bold uppercase text-zinc-500">Top Rated Vendor</p>
          </div>
          <div className="bg-white border border-zinc-200 p-4  print:shadow-none" data-testid="scorecard-top-grade">
            <Award className="w-6 h-6 text-primary mb-2 print:text-zinc-900" />
            <p className="text-2xl font-heading font-bold uppercase tracking-wide">{topVendor?.grade || 'N/A'}</p>
            <p className="text-xs font-bold uppercase text-zinc-500">Top Grade</p>
          </div>
        </div>

        {/* Vendor Rankings Table */}
        <div className="bg-white border border-zinc-200 p-4 sm:p-6  mb-6 print:shadow-none print:p-4" data-testid="scorecard-rankings">
          <h2 className="text-xl font-heading font-bold uppercase tracking-wide mb-4">Vendor Rankings</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="scorecard-table">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500 w-8">#</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500">Vendor</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500 text-center w-16">Grade</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500 text-center w-16">Score</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500 text-right">Total Spend</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500 text-right hidden sm:table-cell">Orders</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500 text-right hidden md:table-cell">Avg Order</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500 text-center hidden lg:table-cell">On-Time</th>
                  <th className="pb-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500 text-center hidden lg:table-cell">Delivery</th>
                </tr>
              </thead>
              <tbody>
                {data.vendors.map((vendor) => {
                  const gc = gradeColor(vendor.grade);
                  return (
                    <tr
                      key={vendor.vendor_name}
                      className="border-b border-gray-200 last:border-b-0 hover:bg-zinc-50 print:hover:bg-transparent"
                      data-testid={`scorecard-row-${vendor.rank}`}
                    >
                      <td className="py-3 pr-3 font-heading font-bold uppercase tracking-wide text-zinc-500">{vendor.rank}</td>
                      <td className="py-3 pr-3">
                        <p className="font-bold">{vendor.vendor_name}</p>
                        <p className="text-xs text-zinc-500">{vendor.spend_share_pct}% of spend &middot; {vendor.unique_products} products</p>
                      </td>
                      <td className="py-3 pr-3 text-center">
                        <span className={`inline-flex items-center justify-center w-10 h-10 text-lg font-heading font-bold uppercase tracking-wide text-white border border-zinc-200 ${gc.bg}`}>
                          {vendor.grade}
                        </span>
                      </td>
                      <td className="py-3 pr-3 text-center font-heading font-bold uppercase tracking-wide text-lg">{vendor.composite_score}</td>
                      <td className="py-3 pr-3 text-right font-bold">{formatCurrency(vendor.total_spent)}</td>
                      <td className="py-3 pr-3 text-right font-bold hidden sm:table-cell">{vendor.order_count}</td>
                      <td className="py-3 pr-3 text-right text-zinc-500 hidden md:table-cell">{formatCurrency(vendor.avg_order_size)}</td>
                      <td className="py-3 pr-3 text-center hidden lg:table-cell">
                        {vendor.on_time_pct !== null ? (
                          <span className={`font-bold ${
                            vendor.on_time_pct >= 80 ? 'text-green-600' :
                            vendor.on_time_pct >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {vendor.on_time_pct}%
                          </span>
                        ) : (
                          <span className="text-zinc-500 text-sm">N/A</span>
                        )}
                      </td>
                      <td className="py-3 text-center hidden lg:table-cell">
                        {vendor.avg_delivery_days !== null ? (
                          <span className={`inline-flex items-center gap-1 text-sm font-bold ${
                            vendor.avg_delivery_days <= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {vendor.avg_delivery_days <= 0 ? (
                              <><TrendingUp className="w-3.5 h-3.5" />{Math.abs(vendor.avg_delivery_days)}d early</>
                            ) : (
                              <><TrendingDown className="w-3.5 h-3.5" />{vendor.avg_delivery_days}d late</>
                            )}
                          </span>
                        ) : (
                          <span className="text-zinc-500 text-sm">N/A</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detailed Vendor Cards */}
        <div className="space-y-4 mb-6 print:mb-0" data-testid="scorecard-detail-cards">
          <h2 className="text-xl font-heading font-bold uppercase tracking-wide">Detailed Breakdown</h2>
          {data.vendors.map((vendor) => {
            const gc = gradeColor(vendor.grade);
            return (
              <div
                key={vendor.vendor_name}
                className={`bg-white border border-zinc-200 p-4 sm:p-6  print:shadow-none print:break-inside-avoid ${gc.light}`}
                data-testid={`scorecard-detail-${vendor.rank}`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center justify-center w-12 h-12 text-xl font-heading font-bold uppercase tracking-wide text-white border border-zinc-200 ${gc.bg}`}>
                      {vendor.grade}
                    </span>
                    <div>
                      <h3 className="text-lg font-heading font-bold uppercase tracking-wide">{vendor.vendor_name}</h3>
                      <p className="text-sm text-zinc-500">Rank #{vendor.rank} &middot; Score {vendor.composite_score}/100</p>
                    </div>
                  </div>
                  <p className="text-xl font-heading font-bold uppercase tracking-wide">{formatCurrency(vendor.total_spent)}</p>
                </div>

                {/* Score Breakdown */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-4">
                  <ScoreBar value={vendor.delivery_score} label="Delivery" />
                  <ScoreBar value={vendor.price_consistency_score} label="Pricing" />
                  <ScoreBar value={Math.min(100, vendor.order_count * 20)} label="Volume" />
                  <ScoreBar value={vendor.frequency_score} label="Frequency" />
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t-2 border-black">
                  <div>
                    <p className="text-xs text-zinc-500 font-bold uppercase">Orders</p>
                    <p className="text-lg font-heading font-bold uppercase tracking-wide">{vendor.order_count}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 font-bold uppercase">Avg Order</p>
                    <p className="text-lg font-heading font-bold uppercase tracking-wide">{formatCurrency(vendor.avg_order_size)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 font-bold uppercase">On-Time</p>
                    <p className="text-lg font-heading font-bold uppercase tracking-wide">
                      {vendor.on_time_pct !== null ? (
                        <span className={
                          vendor.on_time_pct >= 80 ? 'text-green-600' :
                          vendor.on_time_pct >= 60 ? 'text-yellow-600' : 'text-red-600'
                        }>{vendor.on_time_pct}%</span>
                      ) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 font-bold uppercase">Spend Share</p>
                    <p className="text-lg font-heading font-bold uppercase tracking-wide">{vendor.spend_share_pct}%</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Print Footer */}
        <div className="hidden print:block text-center pt-4 border-t border-gray-300 mt-8">
          <p className="text-xs text-zinc-500">
            Generated by Punchlist &middot; {data.company_name} &middot; {formatDate(data.generated_at)}
          </p>
          <p className="text-xs text-zinc-500 mt-1">
            Scoring: Delivery (40%) &middot; Price Consistency (25%) &middot; Order Frequency (20%) &middot; Volume (15%)
          </p>
        </div>
      </div>
    </div>
  );
};

export default VendorScorecardPage;
